<template>
  <div class="ai-call-type-picker">
    <label class="ai-form-question" id="ai-call-type-question">
      What type of call is this?
    </label>
    <p class="ai-form-hint">
      {{ hint }}
    </p>

    <div
      class="ai-type-row"
      role="tablist"
      aria-labelledby="ai-call-type-question"
      @keydown="onTypeKeydown"
    >
      <button
        v-for="option in typeOptions"
        :key="option.id"
        type="button"
        class="ai-type-tile"
        :class="{ 'ai-type-tile--active': isOpen(option.id) }"
        :disabled="disabled"
        :aria-selected="isOpen(option.id) ? 'true' : 'false'"
        :aria-controls="isOpen(option.id) ? 'ai-call-type-panel' : null"
        role="tab"
        @click="toggle(option.id)"
      >
        <span class="ai-type-tile__top">
          <span class="ai-type-tile__icon-wrap" aria-hidden="true">
            <feather-icon :icon="option.icon" size="18" />
          </span>
          <span
            v-if="isOpen(option.id)"
            class="ai-type-tile__check"
            aria-hidden="true"
          >
            <feather-icon icon="CheckIcon" size="12" />
          </span>
        </span>
        <span class="ai-type-tile__copy">
          <span class="ai-type-tile__label">{{ option.label }}</span>
          <span class="ai-type-tile__short">{{ option.short }}</span>
        </span>
      </button>
    </div>

    <p v-if="!hasOpenType" class="ai-type-empty mb-0">
      Choose a type to continue — agent, script, and a test call appear next.
    </p>

    <div
      v-if="hasOpenType"
      id="ai-call-type-panel"
      class="ai-type-panel"
      role="tabpanel"
    >
      <p v-if="selectedOption" class="ai-type-panel__detail">
        <feather-icon :icon="selectedOption.icon" size="16" class="ai-type-panel__detail-icon" />
        <span>{{ selectedOption.detail }}</span>
      </p>
      <div class="ai-type-panel__followup">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AiCallingCallTypePicker',
  props: {
    callType: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    hint: {
      type: String,
      default: 'Pick one. The next questions open below.',
    },
  },
  computed: {
    hasOpenType() {
      return this.typeOptions.some(option => this.isOpen(option.id))
    },
    selectedOption() {
      return this.typeOptions.find(option => this.isOpen(option.id)) || null
    },
  },
  data() {
    return {
      typeOptions: [
        {
          id: 'fresh',
          label: 'Screening',
          short: 'New applicants',
          icon: 'UserCheckIcon',
          detail: 'The AI calls people who just applied, to check interest and the basics.',
        },
        {
          id: 'drive',
          label: 'Drive Followup',
          short: 'After a hiring drive',
          icon: 'CalendarIcon',
          detail: 'The AI follows up with people from a walk-in or campus drive.',
        },
        {
          id: 'other',
          label: 'Other',
          short: 'Something else',
          icon: 'Edit3Icon',
          detail: 'A custom call — describe the goal in the script question below.',
        },
      ],
    }
  },
  methods: {
    isOpen(id) {
      const current = (this.callType || '').toString().trim()
      if (id === 'drive') return current === 'drive' || current === 'follow_up'
      return current === id
    },
    toggle(type) {
      if (this.disabled) return
      if (this.isOpen(type)) return
      this.$emit('update:callType', type)
    },
    onTypeKeydown(event) {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
      if (!keys.includes(event.key) || this.disabled) return
      event.preventDefault()
      const ids = this.typeOptions.map(option => option.id)
      const current = ids.findIndex(id => this.isOpen(id))
      let next = current < 0 ? 0 : current
      if (event.key === 'ArrowRight') next = (next + 1) % ids.length
      if (event.key === 'ArrowLeft') next = (next - 1 + ids.length) % ids.length
      if (event.key === 'Home') next = 0
      if (event.key === 'End') next = ids.length - 1
      this.toggle(ids[next])
      this.$nextTick(() => {
        const buttons = this.$el.querySelectorAll('.ai-type-tile')
        if (buttons[next]) buttons[next].focus()
      })
    },
  },
}
</script>

<style scoped>
.ai-call-type-picker {
  --ai-brand: #00ac99;
  --ai-brand-soft: #e6f7f5;
  --ai-brand-ink: #007a6e;
  --ai-type-gap: 0.75rem;
}
.ai-form-question {
  display: block;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #2f2b3d;
  line-height: 1.35;
  margin-bottom: 0.3rem;
}
.ai-form-hint {
  font-size: 0.875rem;
  line-height: 1.45;
  color: #5e5873;
  margin-bottom: 0.9rem;
}
.ai-type-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--ai-type-gap);
  max-width: 100%;
}
.ai-type-tile {
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.7rem;
  width: 100%;
  min-width: 0;
  min-height: 7.35rem;
  margin: 0;
  padding: 0.95rem 0.95rem 0.9rem;
  overflow: hidden;
  text-align: left;
  border: 1.5px solid rgba(47, 43, 61, 0.14);
  border-radius: 0.65rem;
  background: #fff;
  color: #4b465c;
  font: inherit;
  cursor: pointer;
  box-shadow: none;
  outline: none;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}
.ai-type-tile__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.5rem;
}
.ai-type-tile__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  background: #f3f2f7;
  color: #6e6b7b;
  flex-shrink: 0;
}
.ai-type-tile__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 999px;
  background: var(--ai-brand);
  color: #fff;
  flex-shrink: 0;
}
.ai-type-tile__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  min-width: 0;
  width: 100%;
}
.ai-type-tile__label {
  display: block;
  width: 100%;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.25;
  color: #2f2b3d;
}
.ai-type-tile__short {
  display: -webkit-box;
  width: 100%;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.4;
  color: #5e5873;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}
.ai-type-tile:hover:not(:disabled):not(.ai-type-tile--active) {
  border-color: rgba(0, 172, 153, 0.45);
  background: var(--ai-brand-soft);
  color: var(--ai-brand-ink);
}
.ai-type-tile:hover:not(:disabled):not(.ai-type-tile--active) .ai-type-tile__icon-wrap {
  background: rgba(0, 172, 153, 0.16);
  color: var(--ai-brand-ink);
}
.ai-type-tile:hover:not(:disabled):not(.ai-type-tile--active) .ai-type-tile__label {
  color: var(--ai-brand-ink);
}
.ai-type-tile:hover:not(:disabled):not(.ai-type-tile--active) .ai-type-tile__short {
  color: #4a8a84;
}
.ai-type-tile:focus {
  outline: none;
  box-shadow: none;
}
.ai-type-tile:focus-visible {
  outline: 2px solid var(--ai-brand);
  outline-offset: 2px;
}
.ai-type-tile--active {
  background: var(--ai-brand-soft);
  border-color: var(--ai-brand);
  box-shadow: 0 0 0 1px var(--ai-brand);
  color: var(--ai-brand-ink);
}
.ai-type-tile--active .ai-type-tile__icon-wrap {
  background: rgba(0, 172, 153, 0.2);
  color: var(--ai-brand-ink);
}
.ai-type-tile--active .ai-type-tile__label {
  color: var(--ai-brand-ink);
}
.ai-type-tile--active .ai-type-tile__short {
  color: #4a8a84;
}
.ai-type-tile:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.ai-type-empty {
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
  border: 1px dashed rgba(47, 43, 61, 0.16);
  border-radius: 0.5rem;
  background: #f8f8f8;
  font-size: 0.875rem;
  line-height: 1.45;
  color: #5e5873;
}
.ai-type-panel {
  margin-top: 1rem;
  padding: 1rem 1rem 0.15rem;
  border: 1px solid rgba(47, 43, 61, 0.1);
  border-radius: 0.65rem;
  background: #fafafa;
}
.ai-type-panel__detail {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0 0 1.15rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.45rem;
  background: var(--ai-brand-soft);
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--ai-brand-ink);
}
.ai-type-panel__detail-icon {
  flex-shrink: 0;
  margin-top: 0.1rem;
  color: var(--ai-brand);
}
.ai-type-panel__followup {
  display: flex;
  flex-direction: column;
  gap: 0;
}
@media (max-width: 575.98px) {
  .ai-type-row {
    grid-template-columns: 1fr;
    max-width: none;
  }
  .ai-type-tile {
    flex-direction: row;
    align-items: center;
    min-height: 0;
    padding: 0.8rem 0.9rem;
  }
  .ai-type-tile__top {
    width: auto;
    flex-shrink: 0;
  }
  .ai-type-tile__check {
    position: absolute;
    top: 0.7rem;
    right: 0.7rem;
  }
}
</style>
